import { object, string } from 'yup'
import { useSnackbar } from 'stores/useSnackbar'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormProvider, useForm } from 'react-hook-form'
import { ActivityButton } from 'components/ActivityButton'
import { isPresent } from '@jjvgaming/player-payback-library'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material'
import { grey } from '@mui/material/colors'
import { useGetLicensedEstablishmentUserById } from 'hooks/api/useGetLicensedEstablishmentUserById'
import { FormFieldErrorMessage } from 'components/FormFieldMessage/FormFieldErrorMessage'
import { usePutLicensedEstablishmentUser } from 'hooks/api/usePutLicensedEstablishmentUser'
import { ConfirmLeUserEmailChangeModal } from './ConfirmLeUserEmailChangeModal'
import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useMsal } from '@azure/msal-react'
import { useSetMe } from 'stores/useMeQuery'

const LEUserSchema = object({
  LEUser: object({
    firstName: string().required('First Name is a required field'),
    lastName: string().required('Last Name is a required field'),
    email: string()
      .email('Invalid email')
      .required('Email is a required field'),
  }),
})

export const EditLEUserInformation = ({
  LEUserId,
  isModalOpen,
  toggleModal,
  setIsEditUserModalOpen,
}: {
  LEUserId: number
  isModalOpen: boolean
  toggleModal: () => void
  setIsEditUserModalOpen: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const setMessage = useSnackbar((state) => state.setMessage)
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false)
  const [logoutUser, setLogoutUser] = useState(false)
  const { instance } = useMsal()
  const queryClient = useQueryClient()
  const { passedMe } = useSetMe()
  const meQuery = passedMe
  const currentUser = meQuery.data?.user
  const licensedEstablishmentUserQuery = useGetLicensedEstablishmentUserById({
    licensedEstablishmentUserId: LEUserId,
  })
  const formMethods = useForm({
    resolver: yupResolver(LEUserSchema),
  })

  useEffect(() => {
    if (currentUser?.email === licensedEstablishmentUserQuery.data?.email) {
      setLogoutUser(true)
    }
  }, [licensedEstablishmentUserQuery.data, currentUser])

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = formMethods

  const putLicensedEstablishmentUserMutation = usePutLicensedEstablishmentUser({
    licensedEstablishmentUserId: LEUserId,
    onSuccess: async () => {
      setIsEditUserModalOpen(false)
      setMessage('User has been updated.', 'success')
      setIsEmailModalOpen(false)

      // check to see if the user has changed the email field and if the email is the current user's email
      // if so, log the user out
      if (hasEmailChanged && logoutUser) {
        sessionStorage.removeItem('current-corporate-account')
        try {
          await queryClient.invalidateQueries()
          await instance.logoutRedirect({
            postLogoutRedirectUri: '/',
            account: instance.getActiveAccount(),
          })
        } catch (error) {
          console.log('Failed to Log Out', error)
        }
      }
    },
    onError: async () => {
      setMessage('Error updating J&J Connect User', 'error')
    },
  })

  const handleSubmitWrapper = handleSubmit((data) => {
    const { firstName, lastName, email } = data.LEUser

    if (hasEmailChanged && !isEmailModalOpen) {
      setIsEmailModalOpen(true)
      return
    }

    putLicensedEstablishmentUserMutation.mutate({
      ...leUser,
      firstName,
      lastName,
      email,
      type: leUser.licensedEstablishments?.[0].type ?? 'Staff',
    })
  })

  if (licensedEstablishmentUserQuery.isPending) {
    return <ActivityIndicator />
  }

  if (licensedEstablishmentUserQuery.isError) {
    return <p>An error occurred.</p>
  }

  const leUser = licensedEstablishmentUserQuery.data

  const email = watch('LEUser.email')
  // check to see if the user has changed the email field, if so, open the email change confirmation modal
  const hasEmailChanged =
    email !== undefined && email !== licensedEstablishmentUserQuery.data?.email

  const toggleEmailModalIsOpen = () => {
    setIsEmailModalOpen(!isEmailModalOpen)
    reset({
      LEUser: {
        email: licensedEstablishmentUserQuery.data?.email,
      },
    })
  }

  return (
    <>
      <Dialog
        open={isModalOpen && !isEmailModalOpen}
        maxWidth={'xl'}
        fullWidth
        PaperProps={{ sx: { p: 0, maxWidth: '800px' } }}
      >
        <DialogTitle bgcolor={grey[50]} sx={{ p: 0 }} className="border-b">
          <Box px={4} py={4}>
            <Typography variant="h3" fontWeight={'bold'}>
              Edit Contact Information
            </Typography>
            <Typography variant="body-1" color={'text.secondary'}>
              {`J&J Connect User: ${leUser?.firstName} ${leUser?.lastName}`}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmitWrapper} noValidate className="p-5">
              {errors.LEUser && (
                <Grid item xs={12} pb={4}>
                  <Alert severity="error">
                    Please correct errors to continue
                  </Alert>
                </Grid>
              )}

              <Grid container>
                <Grid item xs={12} md={6} pt={{ md: 2 }}>
                  <InputLabel
                    required
                    htmlFor="LEUser.firstName"
                    sx={{ fontSize: '16px', fontWeight: '100' }}
                  >
                    First Name
                  </InputLabel>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    {...register('LEUser.firstName', { required: true })}
                    defaultValue={leUser?.firstName}
                    error={isPresent(errors.LEUser?.firstName)}
                  />
                  {errors.LEUser?.firstName?.message && (
                    <FormFieldErrorMessage
                      message={errors.LEUser.firstName.message}
                    />
                  )}
                </Grid>
              </Grid>

              <Grid container pt={4}>
                <Grid item xs={12} md={6} pt={{ md: 2 }}>
                  <InputLabel
                    required
                    htmlFor="LEUser.lastName"
                    sx={{ fontSize: '16px', fontWeight: '100' }}
                  >
                    Last Name
                  </InputLabel>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    {...register('LEUser.lastName', { required: true })}
                    defaultValue={leUser?.lastName}
                    error={isPresent(errors.LEUser?.lastName)}
                  />
                  {errors.LEUser?.lastName?.message && (
                    <FormFieldErrorMessage
                      message={errors.LEUser.lastName.message}
                    />
                  )}
                </Grid>
              </Grid>

              <Grid container pt={4} mb={2}>
                <Grid item xs={12} md={6} pt={{ md: 2 }}>
                  <InputLabel
                    required
                    htmlFor="LEUser.email"
                    sx={{ fontSize: '16px', fontWeight: '100' }}
                  >
                    Email
                  </InputLabel>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    {...register('LEUser.email', { required: true })}
                    defaultValue={leUser?.email}
                    error={isPresent(errors.LEUser?.email)}
                  />
                  {errors.LEUser?.email?.message && (
                    <FormFieldErrorMessage
                      message={errors.LEUser.email.message}
                    />
                  )}
                </Grid>
              </Grid>
              <DialogActions className="border-t">
                <Button variant="text" onClick={toggleModal}>
                  Cancel
                </Button>
                <ActivityButton
                  active={putLicensedEstablishmentUserMutation.isPending}
                  type="submit"
                  disabled={!!errors.LEUser}
                  variant="contained"
                >
                  Save
                </ActivityButton>
              </DialogActions>
            </form>
          </FormProvider>
        </DialogContent>
      </Dialog>

      <ConfirmLeUserEmailChangeModal
        newEmail={email}
        isActive={putLicensedEstablishmentUserMutation.isPending}
        isModalOpen={isEmailModalOpen}
        onConfirm={handleSubmitWrapper}
        onCancel={() => {
          toggleEmailModalIsOpen()
          setIsEditUserModalOpen(true)
        }}
      />
    </>
  )
}
